import { useEffect, useState } from 'react';
import { Button, Description, Drawer, FieldError, Spinner, type OverlayTriggerState } from '@heroui/react';
import { AsyncButton } from '../../../components/ui/async-button';
import type { CondominiumFormPayload } from '../../../api/condominiums';
import { DrawerTextField } from '../../../components/ui/drawer-form-field';
import { drawerBodyClassName, drawerFooterClassName, sideDrawerDialogClassName } from '../../../components/ui/side-drawer-classes';
import { sanitizeSlug, slugFromName, suggestUniqueSlug } from '../utils/sanitize-slug';

interface CondominiumFormDrawerProps {
  state: OverlayTriggerState;
  mode: 'create' | 'edit';
  initialValues?: CondominiumFormPayload;
  existingSlugs?: readonly string[];
  onSubmit: (payload: CondominiumFormPayload) => Promise<void>;
}

const emptyForm: CondominiumFormPayload = { name: '', slug: '' };

export function CondominiumFormDrawer({
  state,
  mode,
  initialValues,
  existingSlugs = [],
  onSubmit,
}: CondominiumFormDrawerProps) {
  const [form, setForm] = useState<CondominiumFormPayload>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    if (!state.isOpen) return;
    setForm(initialValues ?? emptyForm);
    setError(null);
    setSubmitting(false);
    setSlugManuallyEdited(false);
  }, [state.isOpen, initialValues]);

  const isValid = form.name.trim().length > 0 && form.slug.trim().length > 0;
  const title = mode === 'create' ? 'Novo condomínio' : 'Editar condomínio';
  const subtitle =
    mode === 'create'
      ? 'Cadastre um novo condomínio no sistema.'
      : 'Atualize nome e identificador (slug).';
  const submitLabel = mode === 'create' ? 'Criar condomínio' : 'Salvar alterações';

  const handleNameChange = (name: string) => {
    if (mode === 'create' && !slugManuallyEdited) {
      const base = slugFromName(name);
      const slug = base ? suggestUniqueSlug(base, existingSlugs) : '';
      setForm({ name, slug });
      return;
    }
    setForm((prev) => ({ ...prev, name }));
  };

  const handleSlugChange = (raw: string) => {
    setSlugManuallyEdited(true);
    setForm((prev) => ({ ...prev, slug: sanitizeSlug(raw) }));
  };

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ name: form.name.trim(), slug: form.slug.trim() });
      state.close();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Não foi possível salvar. Tente novamente.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>{title}</Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>
            <Drawer.Body className={drawerBodyClassName}>
              <Description>{subtitle}</Description>

              <DrawerTextField
                label="Nome do condomínio"
                value={form.name}
                placeholder="Ex: Condomínio Residencial ABC"
                onChange={handleNameChange}
                isDisabled={submitting}
              />

              <DrawerTextField
                label="Slug"
                value={form.slug}
                placeholder="Ex: condominio-abc"
                onChange={handleSlugChange}
                isDisabled={submitting}
                description={
                  mode === 'create' && !slugManuallyEdited
                    ? 'Sugerido automaticamente a partir do nome. Você pode editar.'
                    : 'Apenas letras minúsculas, números e hífens.'
                }
              />

              {error ? <FieldError>{error}</FieldError> : null}
            </Drawer.Body>
            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="ghost" isDisabled={submitting} onPress={state.close}>
                Cancelar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!isValid}
                pendingLabel={submitLabel}
                onPress={handleSubmit}
              >
                {submitLabel}
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
